import { createClient } from "contentful";
import {
	readLanguage,
	readLegalDisclaimerNode,
	readLogo,
	readMenuItems,
	readSocials,
	readSupportLinks
} from "./contentParsers/footer.parser";
import { TypeFooterFields } from "./contentTypes";
import { FooterState } from "../../../components/IframeFooter/ContentTypes";
import {
	documentToHtmlString,
	Options
} from "@contentful/rich-text-html-renderer";
import { Document, INLINES } from "@contentful/rich-text-types";
import { legalDisclaimerRender } from "../../helpers";

const param = new URLSearchParams(window.location.search);
const isPreviewMode = Boolean(param.get("preview"));

const client = createClient({
	space: isPreviewMode
		? process.env.REACT_APP_PREVIEW_CONTENTFUL_SPACE || ""
		: process.env.REACT_APP_CONTENTFUL_SPACE || "",
	environment: isPreviewMode
		? process.env.REACT_APP_PREVIEW_CONTENTFUL_ENV || ""
		: process.env.REACT_APP_CONTENTFUL_ENV || "",
	accessToken: isPreviewMode
		? process.env.REACT_APP_PREVIEW_CONTENTFUL_ACCESS_TOKEN || ""
		: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || "",
	host: isPreviewMode ? "preview.contentful.com" : "cdn.contentful.com"
	/* TODO: in the future, we want to point:
     - iframe prod link to the contentful "published" (cdn.contentful.com) content
     - iframe prep-prod (this does not exist yet) link to contentful "draft + published" (preview.contentful.com) content
     */
	// host:
	//  process.env.REACT_APP_ENVIRONMENT === 'production'
	//      ? 'preview.contentful.com'
	//      : 'cdn.contentful.com'
});

export const useContentful = () => {
	const getEntry = <T>(
		entryId: string,
		locale: string,
		depthLevelToResolveLinks = 10
	) => {
		return client.getEntry<T>(entryId, {
			locale,
			include: depthLevelToResolveLinks
		});
	};

	const getFooterContent = async (
		contentfulEntryId: string,
		locale: string,
		vendor: string | undefined,
		localeCase: (text?: string) => string | undefined
	) => {
		try {
			const entry = await getEntry<TypeFooterFields>(contentfulEntryId, locale);

			const legalDisclaimerLinkOption: Options = {
				renderNode: {
					[INLINES.HYPERLINK]: (node) => legalDisclaimerRender(node)
				}
			};

			return {
				menuItems: readMenuItems(
					entry?.fields?.siteNavigation,
					localeCase,
					vendor
				),
				socials: readSocials(
					entry?.fields?.socialNavigation,
					localeCase,
					vendor
				),
				logo: readLogo(entry?.fields?.brandLink, vendor),
				legalDisclaimer: documentToHtmlString(
					entry?.fields?.legalDisclaimer as Document,
					legalDisclaimerLinkOption
				),
				legalDisclaimerNode: readLegalDisclaimerNode(
					entry?.fields?.legalDisclaimer as Document,
					vendor
				),
				supportLinks: readSupportLinks(
					entry?.fields?.supportNavigation,
					localeCase,
					vendor
				),
				languageParams: readLanguage(
					entry?.fields?.languageNavigation,
					localeCase,
					vendor
				)
			} as FooterState;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(`Error fetching footer ${error}`);
		}
	};

	return {
		getEntry,
		getFooterContent
	};
};
